import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  FileText, 
  Upload,
  CheckCircle,
  Loader2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const membershipTypes = [
  { value: 'full', label: 'Full Membership - €300/year' },
  { value: 'associate', label: 'Associate Membership - €150/year' },
];

const countries = [
  'Afghanistan', 'Albania', 'Algeria', 'Argentina', 'Australia', 'Austria', 'Bangladesh',
  'Belgium', 'Brazil', 'Canada', 'China', 'Colombia', 'Czech Republic', 'Denmark', 'Egypt',
  'Finland', 'France', 'Germany', 'Greece', 'Hungary', 'India', 'Indonesia', 'Iran', 'Iraq',
  'Ireland', 'Israel', 'Italy', 'Japan', 'Jordan', 'Kenya', 'Korea, South', 'Kuwait',
  'Lebanon', 'Malaysia', 'Mexico', 'Morocco', 'Netherlands', 'New Zealand', 'Nigeria',
  'Norway', 'Pakistan', 'Philippines', 'Poland', 'Portugal', 'Qatar', 'Romania', 'Russia',
  'Saudi Arabia', 'Singapore', 'South Africa', 'Spain', 'Sweden', 'Switzerland', 'Thailand',
  'Turkey', 'Ukraine', 'United Arab Emirates', 'United Kingdom', 'United States', 'Vietnam',
];

export default function ApplicationPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [applicationId, setApplicationId] = useState(null);
  const [files, setFiles] = useState([]);
  const [formData, setFormData] = useState({
    institution_name: '',
    position_title: '',
    country: '',
    email: '',
    phone: '',
    website: '',
    membership_type: '',
    motivation_message: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    if (e.target.files) {
      setFiles(Array.from(e.target.files));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.institution_name || !formData.country || !formData.email || 
        !formData.phone || !formData.membership_type || !formData.motivation_message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      // Submit application
      const response = await axios.post(`${API}/applications`, formData);
      const appId = response.data.application_id;
      setApplicationId(appId);

      // Upload documents if any
      for (const file of files) {
        const formDataFile = new FormData();
        formDataFile.append('file', file);
        await axios.post(`${API}/applications/${appId}/documents`, formDataFile, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      setSubmitted(true);
      toast.success('Application submitted successfully!');
    } catch (error) {
      console.error('Submission error:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center bg-slate-50">
        <div className="max-w-md w-full mx-auto p-8 bg-white rounded-sm shadow-lg text-center" data-testid="application-success">
          <div className="h-16 w-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#0E285B] mb-4">
            Application Submitted!
          </h2>
          <p className="text-slate-600 mb-4">
            Thank you for your application. Our team will review your submission 
            and contact you at <strong>{formData.email}</strong> within 5-7 business days.
          </p>
          <p className="text-sm text-slate-500 mb-6">
            Application ID: <span className="font-mono">{applicationId}</span>
          </p>
          <Button 
            onClick={() => navigate('/')}
            className="bg-[#0E285B] text-white hover:bg-[#0E285B]/90"
            data-testid="back-home-btn"
          >
            Back to Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-[#0E285B] py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 mb-6">
              <FileText className="h-6 w-6 text-[#F5A623]" />
              <span className="text-[#F5A623] font-medium uppercase tracking-wider text-sm">
                Membership Application
              </span>
            </div>
            <h1 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-white leading-tight mb-4">
              Apply for UIEHEC Membership
            </h1>
            <p className="text-slate-300">
              Complete the form below to begin your membership application process.
            </p>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <form onSubmit={handleSubmit} className="bg-white p-8 md:p-12 rounded-sm shadow-lg border border-slate-100">
            <div className="space-y-8">
              {/* Institution Details */}
              <div>
                <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#0E285B] mb-6 pb-2 border-b border-slate-200">
                  Institution Details
                </h3>
                <div className="grid gap-6">
                  <div>
                    <Label htmlFor="institution_name" className="text-[#0E285B]">
                      Institution / Full Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="institution_name"
                      name="institution_name"
                      value={formData.institution_name}
                      onChange={handleChange}
                      className="mt-2 h-11 border-slate-200 focus:ring-[#F5A623] focus:border-[#F5A623]"
                      placeholder="Enter your institution name"
                      data-testid="institution-name-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="position_title" className="text-[#0E285B]">
                      Position / Title
                    </Label>
                    <Input
                      id="position_title"
                      name="position_title"
                      value={formData.position_title}
                      onChange={handleChange}
                      className="mt-2 h-11 border-slate-200 focus:ring-[#F5A623] focus:border-[#F5A623]"
                      placeholder="e.g., President, Dean, Director"
                      data-testid="position-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="country" className="text-[#0E285B]">
                      Country <span className="text-red-500">*</span>
                    </Label>
                    <Select onValueChange={(value) => handleSelectChange('country', value)}>
                      <SelectTrigger className="mt-2 h-11 border-slate-200" data-testid="country-select">
                        <SelectValue placeholder="Select your country" />
                      </SelectTrigger>
                      <SelectContent>
                        {countries.map((country) => (
                          <SelectItem key={country} value={country}>
                            {country}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Contact Details */}
              <div>
                <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#0E285B] mb-6 pb-2 border-b border-slate-200">
                  Contact Details
                </h3>
                <div className="grid gap-6">
                  <div className="grid gap-6 md:grid-cols-2">
                    <div>
                      <Label htmlFor="email" className="text-[#0E285B]">
                        Email <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="mt-2 h-11 border-slate-200 focus:ring-[#F5A623] focus:border-[#F5A623]"
                        placeholder="contact@institution.edu"
                        data-testid="email-input"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="phone" className="text-[#0E285B]">
                        Phone Number <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        className="mt-2 h-11 border-slate-200 focus:ring-[#F5A623] focus:border-[#F5A623]"
                        placeholder="+49 123 456 7890"
                        data-testid="phone-input"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="website" className="text-[#0E285B]">
                      Website
                    </Label>
                    <Input
                      id="website"
                      name="website"
                      value={formData.website}
                      onChange={handleChange}
                      className="mt-2 h-11 border-slate-200 focus:ring-[#F5A623] focus:border-[#F5A623]"
                      placeholder="https://www.institution.edu"
                      data-testid="website-input"
                    />
                  </div>
                </div>
              </div>

              {/* Membership Details */}
              <div>
                <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#0E285B] mb-6 pb-2 border-b border-slate-200">
                  Membership Details
                </h3>
                <div className="space-y-6">
                  <div>
                    <Label htmlFor="membership_type" className="text-[#0E285B]">
                      Requested Membership Type <span className="text-red-500">*</span>
                    </Label>
                    <Select onValueChange={(value) => handleSelectChange('membership_type', value)}>
                      <SelectTrigger className="mt-2 h-11 border-slate-200" data-testid="membership-type-select">
                        <SelectValue placeholder="Select membership type" />
                      </SelectTrigger>
                      <SelectContent>
                        {membershipTypes.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="motivation_message" className="text-[#0E285B]">
                      Motivation Message <span className="text-red-500">*</span>
                    </Label>
                    <Textarea
                      id="motivation_message"
                      name="motivation_message"
                      value={formData.motivation_message}
                      onChange={handleChange}
                      className="mt-2 min-h-[150px] border-slate-200 focus:ring-[#F5A623] focus:border-[#F5A623]"
                      placeholder="Please describe why you wish to join UIEHEC and what you hope to contribute to our community..."
                      data-testid="motivation-textarea"
                    />
                  </div>
                </div>
              </div>

              {/* Document Upload */}
              <div>
                <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#0E285B] mb-6 pb-2 border-b border-slate-200">
                  Supporting Documents
                </h3>
                <div className="border-2 border-dashed border-slate-200 rounded-sm p-8 text-center">
                  <Upload className="h-10 w-10 text-slate-400 mx-auto mb-4" />
                  <p className="text-slate-600 mb-2">
                    Upload supporting documents (optional)
                  </p>
                  <p className="text-sm text-slate-500 mb-4">
                    Accreditation certificates, institutional brochures, etc.
                  </p>
                  <input
                    type="file"
                    multiple
                    onChange={handleFileChange}
                    className="hidden"
                    id="file-upload"
                    data-testid="file-upload-input"
                  />
                  <label htmlFor="file-upload">
                    <Button type="button" variant="outline" asChild>
                      <span>Choose Files</span>
                    </Button>
                  </label>
                  {files.length > 0 && (
                    <div className="mt-4 text-left">
                      <p className="text-sm font-medium text-slate-700 mb-2">Selected files:</p>
                      <ul className="text-sm text-slate-600 space-y-1">
                        {files.map((file, index) => (
                          <li key={index} className="flex items-center gap-2">
                            <FileText className="h-4 w-4" />
                            {file.name}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>

              {/* Submit Button */}
              <div className="pt-4">
                <Button 
                  type="submit" 
                  className="w-full bg-[#0E285B] text-white hover:bg-[#0E285B]/90 h-12 text-lg rounded-sm"
                  disabled={loading}
                  data-testid="submit-application-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'Submit Application'
                  )}
                </Button>
                <p className="text-sm text-slate-500 text-center mt-4">
                  By submitting this application, you agree to our terms and conditions.
                </p>
              </div>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
